import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material'
import { DeleteOutline, MoreVert } from '@mui/icons-material'
import { usePopover } from '../hooks/use-popover'

const MENU_ICON_MIN_WIDTH = 26

export interface RowMenuAction {
  label: string
  onClick(): void
  disabled?: boolean
}

interface RowActionsMenuProps {
  actions?: RowMenuAction[]
  // Shown last, in red, after the other actions
  removeAction?: RowMenuAction
  // Injected by the grid's actions cell to keep its keyboard focus navigation
  tabIndex?: number
}

const RowActionsMenu = ({
  actions = [],
  removeAction,
  tabIndex,
}: RowActionsMenuProps) => {
  const { t } = useTranslation()
  const [anchorEl, isMenuOpen, openMenu, closeMenu] = usePopover()
  // Runs once the menu has closed, so returning focus to the menu button
  // doesn't clash with a dialog the action opens
  const pendingActionRef = useRef<(() => void) | null>(null)

  const runAndClose = (action: RowMenuAction) => () => {
    pendingActionRef.current = action.onClick
    closeMenu()
  }

  const handleMenuExited = () => {
    pendingActionRef.current?.()
    pendingActionRef.current = null
  }

  return (
    <>
      <IconButton
        size="small"
        aria-label={t('moreActions')}
        aria-haspopup="true"
        aria-expanded={isMenuOpen ? 'true' : undefined}
        tabIndex={tabIndex}
        onClick={openMenu}
      >
        <MoreVert />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionProps={{ onExited: handleMenuExited }}
      >
        {actions.map(action => (
          <MenuItem
            key={action.label}
            onClick={runAndClose(action)}
            disabled={action.disabled}
          >
            <ListItemText>{action.label}</ListItemText>
          </MenuItem>
        ))}

        {removeAction && actions.length > 0 && <Divider />}

        {removeAction && (
          <MenuItem
            onClick={runAndClose(removeAction)}
            disabled={removeAction.disabled}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon
              sx={{ minWidth: MENU_ICON_MIN_WIDTH, color: 'inherit' }}
            >
              <DeleteOutline fontSize="small" />
            </ListItemIcon>
            <ListItemText>{removeAction.label}</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  )
}

export default RowActionsMenu
